import { Button, Input, Select, SelectItem } from '@heroui/react';
import { Plus, Trash2, Minus } from 'lucide-react';
import { useBanquetteSlotStore } from '@/store/banquetteSlotStore';
import type { MaterialLibrary } from '@estimator/shared';

interface BanquetteSlotListProps {
  materials: MaterialLibrary[];
  productType?: string;
  profileDepth?: number;
  profileHeight?: number;
}

export default function BanquetteSlotList({ materials, productType, profileDepth }: BanquetteSlotListProps) {
  const { slots, activeSlotId, setActiveSlot, addSlot, updateSlot, removeSlot, removePolyline } =
    useBanquetteSlotStore();

  const isFabricMaterial = (materialId: string | null): boolean => {
    if (!materialId) return false;
    const mat = materials.find((m) => m.id === materialId);
    if (!mat) return false;
    return !!(mat as any).fabric_subtype || (mat.material_type ?? '').toLowerCase() === 'fabric';
  };

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--bg-surface)' }}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b" style={{ borderColor: 'var(--border)' }}>
        <span className="text-xs font-semibold" style={{ color: '#e67e22' }}>Finish Slots</span>
        <Button size="sm" variant="light" onPress={() => addSlot('front')} startContent={<Plus size={12} />}>
          Add
        </Button>
      </div>

      {/* Slot list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {slots.length === 0 && (
          <div className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>
            No slots yet. Click "Add" to create one, then draw polylines on the drawing.
          </div>
        )}

        {slots.map((slot) => {
          const isActive = slot.id === activeSlotId;
          const totalPx = slot.polylines.reduce((sum, p) => sum + p.length_px, 0);

          return (
            <div
              key={slot.id}
              className="rounded-lg p-2 cursor-pointer transition-colors"
              style={{
                backgroundColor: isActive ? 'var(--bg-hover)' : 'var(--bg-panel)',
                border: isActive ? `1px solid ${slot.color}` : '1px solid var(--border)',
              }}
              onClick={() => setActiveSlot(slot.id)}
            >
              {/* Color + Name */}
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: slot.color }}
                />
                <Input
                  value={slot.name}
                  onValueChange={(v) => updateSlot(slot.id, { name: v })}
                  size="sm"
                  className="flex-1"
                  classNames={{ inputWrapper: 'bg-transparent h-6 min-h-6', input: 'text-xs' }}
                  onClick={(e) => e.stopPropagation()}
                />
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onPress={() => removeSlot(slot.id)}
                  className="min-w-6 w-6 h-6"
                >
                  <Trash2 size={12} style={{ color: 'var(--text-muted)' }} />
                </Button>
              </div>

              {/* Material */}
              <Select
                selectedKeys={slot.material_id ? [slot.material_id] : []}
                onSelectionChange={(keys) => {
                  const newMatId = (Array.from(keys)[0] as string) || null;
                  updateSlot(slot.id, { material_id: newMatId });
                }}
                size="sm"
                placeholder="Material..."
                className="mb-1"
                classNames={{ trigger: 'bg-[var(--bg-panel)] h-7 min-h-7' }}
                onClick={(e) => e.stopPropagation()}
              >
                {materials.map((m) => (
                  <SelectItem key={m.id}>{m.name}</SelectItem>
                ))}
              </Select>

              {/* Calc mode toggle */}
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Mode:</span>
                <button
                  className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: slot.calc_mode === 'linear' ? slot.color + '33' : 'transparent',
                    color: slot.calc_mode === 'linear' ? slot.color : 'var(--text-muted)',
                    border: `1px solid ${slot.calc_mode === 'linear' ? slot.color : 'var(--border)'}`,
                  }}
                  onClick={(e) => { e.stopPropagation(); updateSlot(slot.id, { calc_mode: 'linear' }); }}
                >
                  Linear
                </button>
                <button
                  className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: slot.calc_mode === 'area' ? slot.color + '33' : 'transparent',
                    color: slot.calc_mode === 'area' ? slot.color : 'var(--text-muted)',
                    border: `1px solid ${slot.calc_mode === 'area' ? slot.color : 'var(--border)'}`,
                  }}
                  onClick={(e) => { e.stopPropagation(); updateSlot(slot.id, { calc_mode: 'area' }); }}
                >
                  Area
                </button>
              </div>

              {/* Fabric Settings */}
              {isFabricMaterial(slot.material_id) && (() => {
                const autoWrap = productType === 'banquette_base' ? 0 : (profileDepth ?? 0);
                return (
                  <div className="mt-1 mb-1 p-1.5 rounded" style={{ backgroundColor: 'var(--bg-base)', border: '1px solid var(--border)' }}>
                    <span className="text-[10px] font-semibold block mb-1" style={{ color: '#e67e22' }}>Fabric Settings</span>
                    <div className="grid grid-cols-2 gap-1">
                      <div>
                        <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>Wrap (in)</span>
                        <input
                          type="number"
                          value={slot.fabric_wrap_value ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value;
                            updateSlot(slot.id, {
                              fabric_wrap_value: raw === '' ? null : (parseFloat(raw) || 0),
                            });
                          }}
                          placeholder={`auto: ${autoWrap}`}
                          className="w-full text-[10px] rounded px-1 py-0.5"
                          style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                          step="0.25"
                          min="0"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div>
                        <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>Staple (in)</span>
                        <input
                          type="number"
                          value={slot.fabric_staple_allowance ?? 1}
                          onChange={(e) => updateSlot(slot.id, { fabric_staple_allowance: parseFloat(e.target.value) || 0 })}
                          className="w-full text-[10px] rounded px-1 py-0.5"
                          style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                          step="0.25"
                          min="0"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Polylines */}
              {(() => {
                const totalReal = slot.polylines.reduce((sum, p) => sum + (p.length_real ?? 0), 0);
                const unit = slot.polylines.find(p => p.length_unit)?.length_unit;
                const hasReal = totalReal > 0 && unit;
                return (
                  <div className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                    {slot.polylines.length} polyline{slot.polylines.length !== 1 ? 's' : ''}
                    {hasReal
                      ? ` (${totalReal.toFixed(1)} ${unit} total)`
                      : totalPx > 0 ? ` (${totalPx.toFixed(0)}px total)` : ''}
                  </div>
                );
              })()}
              {isActive && slot.polylines.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {slot.polylines.map((pl, i) => (
                    <div key={i} className="flex items-center justify-between text-[10px]">
                      <span style={{ color: 'var(--text-muted)' }}>
                        #{i + 1}: {pl.length_real != null && pl.length_unit
                          ? `${pl.length_real.toFixed(1)} ${pl.length_unit}`
                          : `${pl.length_px.toFixed(0)}px`} ({pl.points.length} pts)
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); removePolyline(slot.id, i); }}
                        className="hover:text-red-400"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        <Minus size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
